import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, AlertTriangle, FileText, Ban, AlertOctagon, ArrowRight, ExternalLink, XCircle } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const ShouldITalkToInsurancePage = () => {
  const faqData = [
    {
      question: "Am I required to give a recorded statement to the other driver's insurance company?",
      answer: "No. You are not legally required to give a recorded statement to the other driver's insurance company. You can politely decline or say you'll provide information through your attorney."
    },
    {
      question: "What if the insurance adjuster says they can't process my claim without a statement?",
      answer: "This is a common pressure tactic. They can process claims based on police reports, medical records, and other documentation. A recorded statement is for their benefit, not yours."
    },
    {
      question: "Should I talk to my own insurance company?",
      answer: "Yes, you should report the accident to your own insurance company. However, even with your own insurer, be careful about detailed statements until you understand your injuries fully."
    },
    {
      question: "What questions do insurance adjusters ask in recorded statements?",
      answer: "They ask questions designed to find inconsistencies or admissions that can reduce your claim—questions about your speed, whether you saw the other car, your prior injuries, and how you're feeling now."
    },
    {
      question: "Can I change my statement later if I make a mistake?",
      answer: "Recorded statements become part of your claim file. Changing your account later can be used to argue you're not credible. This is why it's risky to give a statement before you fully understand your injuries."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="should-i-talk-to-insurance-page">
      <SEOHead 
        title="Should I Talk to Insurance After an Accident? | Amy C. Brown Law"
        description="Should you give a recorded statement to the insurance company after a car accident? Learn why adjusters ask for statements and how to protect yourself. Call (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Insurance Claim Guidance",
          description: "Legal guidance on communicating with insurance companies after a car accident. Protecting your rights during the claims process.",
          category: "Personal Injury Law - Insurance Claims"
        }}
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <AlertOctagon className="w-4 h-4" />
                Important Warning
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-6 leading-tight">
                Should I Talk to the Insurance Company After an Accident?
              </h1>
              
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                The insurance adjuster called. They're friendly, sympathetic, and asking for a "recorded statement." Before you say anything, you need to understand what's really happening.
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-8 border-l-4 border-red-500">
                <p className="text-slate-700 leading-relaxed font-medium">
                  I spent years defending insurance companies. I know exactly why they want that recorded statement—and it's not to help you. Let me explain.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a 
                  href="tel:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Call (425) 675-1224
                </a>
                <a 
                  href="sms:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold text-lg hover:bg-secondary hover:text-white transition-all"
                  data-testid="hero-text-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me First
                </a>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Free consultation
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Former insurance defense attorney
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100">
              <h3 className="font-heading text-xl font-bold text-secondary mb-6">The Short Answer:</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-red-50 rounded-xl">
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-secondary">Other driver's insurance?</p>
                    <p className="text-slate-600 text-sm">You are NOT required to give them a recorded statement. Be very careful.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-secondary">Your own insurance?</p>
                    <p className="text-slate-600 text-sm">Report the accident, but be cautious about detailed statements until you understand your injuries.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why They Want a Statement */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            Why Insurance Companies Ask for Recorded Statements
          </h2>
          
          <div className="bg-slate-50 rounded-2xl p-8 mb-8">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Insurance adjusters are trained professionals. Their job is to close claims for as little money as possible. The recorded statement is one of their most effective tools.
            </p>
            <p className="text-slate-600 leading-relaxed">
              When I worked on the defense side, I saw how these statements were used. Even innocent, honest answers can be twisted to reduce what you're owed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-secondary mb-3">They're Looking For:</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Inconsistencies they can use to question your credibility
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Admissions of any fault, even partial ("I didn't see them until...")
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Statements about feeling "fine" or "okay" right after the crash
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Information about prior injuries or accidents
                </li>
              </ul>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-secondary mb-3">Common Trap Questions:</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  "How are you feeling today?" (If you say "fine," they'll use it)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  "Were you wearing your seatbelt?"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  "At what point did you first see the other vehicle?"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  "Have you ever had back/neck problems before?"
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Real Example Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            How "Innocent" Answers Hurt Claims
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Example 1</div>
              </div>
              <p className="text-slate-700 mt-4 mb-2"><strong>Adjuster asks:</strong> "How are you feeling today?"</p>
              <p className="text-slate-700 mb-2"><strong>You say:</strong> "I'm doing okay, just a little sore."</p>
              <p className="text-slate-700"><strong>How it's used:</strong> "The claimant stated she was 'okay' just three days after the accident, indicating her injuries were minor."</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Example 2</div>
              </div>
              <p className="text-slate-700 mt-4 mb-2"><strong>Adjuster asks:</strong> "When did you first see the other car?"</p>
              <p className="text-slate-700 mb-2"><strong>You say:</strong> "I saw them right before they hit me."</p>
              <p className="text-slate-700"><strong>How it's used:</strong> "The claimant admits she saw the vehicle before impact, raising questions about whether she could have taken evasive action."</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Example 3</div>
              </div>
              <p className="text-slate-700 mt-4 mb-2"><strong>Adjuster asks:</strong> "Have you ever had any back problems before?"</p>
              <p className="text-slate-700 mb-2"><strong>You say:</strong> "I tweaked my back years ago but it healed."</p>
              <p className="text-slate-700"><strong>How it's used:</strong> "The claimant has a pre-existing back condition. Current symptoms may be related to this prior injury rather than the accident."</p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Should Do */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            What You Should Do Instead
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <h3 className="font-heading font-semibold text-secondary mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Do This:
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li>• Politely decline the recorded statement</li>
                <li>• Say: "I'll provide information through my attorney"</li>
                <li>• Or: "I need time to consult with a lawyer first"</li>
                <li>• Get their contact information for follow-up</li>
                <li>• Report the accident to YOUR insurance</li>
                <li>• Talk to a lawyer before giving any detailed statement</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <h3 className="font-heading font-semibold text-secondary mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Avoid This:
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li>• Giving a recorded statement without legal advice</li>
                <li>• Saying you feel "fine" or "okay"</li>
                <li>• Guessing about speeds, distances, or times</li>
                <li>• Discussing prior injuries or accidents</li>
                <li>• Signing medical release authorizations</li>
                <li>• Accepting a quick settlement offer</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Note */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-primary">
            <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
              Why I Know This
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              I started my career at a defense firm representing insurance companies and large employers. I saw firsthand how recorded statements were analyzed, how inconsistencies were highlighted, and how innocent answers were used to reduce claims.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Now I use that knowledge to protect people like you. When insurance adjusters call my clients, they go through me. I know their playbook because I used to be on their team.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If an adjuster is pressuring you for a statement, call me. I'll tell you exactly what to say—and what not to say.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Insurance Statements
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts - Entity SEO */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Insurance Claims
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Insurance_bad_faith" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Insurance Bad Faith <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Claims_adjuster" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Claims Adjuster <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Subrogation" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Subrogation <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Personal Injury <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Related Car Accident Resources */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Related Car Accident Resources
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/car-crashes" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <FileText className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Car Accident Claims</p>
                <p className="text-sm text-slate-500">Overview of the car accident claim process</p>
              </div>
            </Link>
            <Link to="/what-to-do-after-car-accident" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <CheckCircle className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">What To Do After an Accident</p>
                <p className="text-sm text-slate-500">Step-by-step guide for Washington</p>
              </div>
            </Link>
            <Link to="/should-i-accept-settlement" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <AlertTriangle className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Settlement Offers</p>
                <p className="text-sm text-slate-500">How to evaluate what they're offering</p>
              </div>
            </Link>
            <Link to="/everett-car-accident-lawyer" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <ArrowRight className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Everett Car Accident Lawyer</p>
                <p className="text-sm text-slate-500">Local representation in Snohomish County</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Insurance Calling? Talk to Me First.
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            I'll tell you exactly what to say—and what not to say. No charge for the conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShouldITalkToInsurancePage;
